import { api } from '../../features/auth/authStore';

// Types
export interface LeaveType {
  _id: string;
  id?: string; // For backward compatibility
  name: string;
  description?: string;
  maxDaysPerYear: number;
  carryForward: boolean;
  maxCarryForwardDays?: number;
  requiresApproval: boolean;
  color: string;
  advanceNoticeRequired?: number;
  isActive: boolean;
}

export interface LeaveApplication {
  id: string;
  userId: string;
  leaveTypeId: string;
  startDate: string;
  endDate: string;
  reason: string;
  isHalfDay: boolean;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  appliedAt: string;
  approvalWorkflow: {
    currentLevel: number;
    levels: Array<{
      level: number;
      approverId: string | {
        _id: string;
        firstName: string;
        lastName: string;
        roles: string[];
      };
      approverRole: string;
      status: 'pending' | 'approved' | 'rejected';
      processedAt?: string;
      comments?: string;
    }>;
  };
  rejectionReason?: string;
  leaveType?: LeaveType;
  user?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

// Backend response interface (what the API actually returns)
interface BackendLeaveApplication {
  _id: string;
  userId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | string;
  leaveTypeId: {
    _id: string;
    name: string;
    color: string;
  } | string;
  startDate: string;
  endDate: string;
  reason: string;
  isHalfDay: boolean;
  status: string; // lowercase: 'pending', 'approved', 'rejected', 'cancelled'
  appliedAt: string;
  approvalWorkflow: {
    currentLevel: number;
    levels: Array<{
      level: number;
      approverId: string;
      approverRole: string;
      status: string; // lowercase
      processedAt?: string;
      comments?: string;
    }>;
  };
  rejectionReason?: string;
}

// Transform backend response to frontend interface
const transformLeaveApplication = (backendApp: BackendLeaveApplication): LeaveApplication => {
  return {
    id: backendApp._id,
    userId: typeof backendApp.userId === 'string' ? backendApp.userId : backendApp.userId._id,
    leaveTypeId: typeof backendApp.leaveTypeId === 'string' ? backendApp.leaveTypeId : backendApp.leaveTypeId._id,
    startDate: backendApp.startDate,
    endDate: backendApp.endDate,
    reason: backendApp.reason,
    isHalfDay: backendApp.isHalfDay,
    status: backendApp.status.toUpperCase() as 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED',
    appliedAt: backendApp.appliedAt,
    approvalWorkflow: {
      currentLevel: backendApp.approvalWorkflow.currentLevel,
      levels: backendApp.approvalWorkflow.levels.map(level => ({
        ...level,
        status: level.status as 'pending' | 'approved' | 'rejected'
      }))
    },
    rejectionReason: backendApp.rejectionReason,
    // Map populated fields
    user: typeof backendApp.userId === 'object' ? {
      firstName: backendApp.userId.firstName,
      lastName: backendApp.userId.lastName,
      email: backendApp.userId.email
    } : undefined,
    leaveType: typeof backendApp.leaveTypeId === 'object' ? {
      id: backendApp.leaveTypeId._id,
      name: backendApp.leaveTypeId.name,
      color: backendApp.leaveTypeId.color,
      isActive: true, // Default value
      advanceNoticeRequired: 0 // Default value
    } : undefined
  };
};

export interface LeaveBalance {
  id: string;
  userId: string;
  leaveTypeId: string;
  year: number;
  totalAllotted: number;
  used: number;
  pending: number;
  available: number;
  carriedForward: number;
  leaveType?: LeaveType;
  // Backward compatibility aliases
  totalDays?: number;
  usedDays?: number;
  pendingDays?: number;
  availableDays?: number;
  carriedForwardDays?: number;
}

export interface Holiday {
  _id: string;
  id?: string; // For backward compatibility
  name: string;
  description?: string;
  date: string;
  type: 'fixed' | 'floating' | 'optional';
  isRecurring: boolean;
  year: number;
  isActive: boolean;
  color: string;
}

export interface LeaveApplicationRequest {
  leaveTypeId: string;
  startDate: string;
  endDate: string;
  reason: string;
  isHalfDay?: boolean;
}

export interface LeaveApprovalRequest {
  action: 'approve' | 'reject';
  comments?: string;
  rejectionReason?: string;
}

export interface HolidayRequest {
  name: string;
  description?: string;
  date: string;
  type: 'fixed' | 'floating' | 'optional';
  isRecurring: boolean;
  color?: string;
}

export interface LeaveListQuery {
  page?: number;
  limit?: number;
  userId?: string;
  leaveTypeId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface HolidayListQuery {
  page?: number;
  limit?: number;
  year?: number;
  type?: string;
  isActive?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export const leavesApi = {
  // Leave Applications
  applyForLeave: async (data: LeaveApplicationRequest): Promise<LeaveApplication> => {
    const response = await api.post('/leaves/apply', data);
    return response.data.data; // Backend returns { success: true, data: {...} }
  },

  getMyLeaveApplications: async (params?: LeaveListQuery): Promise<{
    applications: LeaveApplication[];
    total: number;
    page: number;
    limit: number;
  }> => {
    const response = await api.get('/leaves/my-applications', { params });
    const backendData = response.data.data;
    return {
      ...backendData,
      applications: backendData.applications.map(transformLeaveApplication)
    };
  },

  getLeaveApplications: async (params?: LeaveListQuery): Promise<{
    applications: LeaveApplication[];
    total: number;
    page: number;
    limit: number;
  }> => {
    const response = await api.get('/leaves', { params });
    const backendData = response.data.data;
    return {
      ...backendData,
      applications: backendData.applications.map(transformLeaveApplication)
    };
  },

  getLeaveApplicationById: async (id: string): Promise<LeaveApplication> => {
    const response = await api.get(`/leaves/${id}`);
    return transformLeaveApplication(response.data.data);
  },

  processLeaveApproval: async (id: string, data: LeaveApprovalRequest): Promise<LeaveApplication> => {
    const response = await api.post(`/leaves/${id}/approve`, data);
    return transformLeaveApplication(response.data.data);
  },

  // Leave Balance
  getLeaveBalance: async (params?: { year?: number; userId?: string }): Promise<LeaveBalance[]> => {
    const response = await api.get('/leaves/balance', { params });
    return response.data.data || [];
  },

  initializeLeaveBalances: async (data: { userId?: string; year?: number }): Promise<LeaveBalance[]> => {
    const response = await api.post('/leaves/balance/initialize', data);
    return response.data.data; // Backend returns { success: true, data: {...} }
  },

  // Leave Types
  getLeaveTypes: async (): Promise<LeaveType[]> => {
    const response = await api.get('/leaves/types');
    return response.data.data || [];
  },

  createLeaveType: async (leaveTypeData: Partial<LeaveType>): Promise<LeaveType> => {
    const response = await api.post('/leaves/types', leaveTypeData);
    return response.data.data;
  },

  updateLeaveType: async (id: string, leaveTypeData: Partial<LeaveType>): Promise<LeaveType> => {
    const response = await api.put(`/leaves/types/${id}`, leaveTypeData);
    return response.data.data;
  },

  deleteLeaveType: async (id: string): Promise<void> => {
    await api.delete(`/leaves/types/${id}`);
  },

  createDefaultLeaveTypes: async (): Promise<LeaveType[]> => {
    const response = await api.post('/leaves/types/default');
    return response.data.data || response.data;
  },

  // Holidays
  createHoliday: async (data: HolidayRequest): Promise<Holiday> => {
    const response = await api.post('/leaves/holidays', data);
    return response.data.data || response.data;
  },

  updateHoliday: async (id: string, data: Partial<HolidayRequest>): Promise<Holiday> => {
    const response = await api.put(`/leaves/holidays/${id}`, data);
    return response.data.data || response.data;
  },

  deleteHoliday: async (id: string): Promise<void> => {
    await api.delete(`/leaves/holidays/${id}`);
  },

  getHolidays: async (params?: HolidayListQuery): Promise<{
    holidays: Holiday[];
    total: number;
    page: number;
    limit: number;
  }> => {
    const response = await api.get('/leaves/holidays', { params });
    return response.data.data || response.data;
  },

  getHolidaysInRange: async (startDate: string, endDate: string): Promise<Holiday[]> => {
    const response = await api.get('/leaves/holidays/range', {
      params: { startDate, endDate }
    });
    return response.data.data || response.data;
  },

  checkHoliday: async (date: string): Promise<{
    isHoliday: boolean;
    holiday?: Holiday;
  }> => {
    const response = await api.get(`/leaves/holidays/check/${date}`);
    return response.data.data || response.data;
  },

  createDefaultHolidays: async (year?: number): Promise<Holiday[]> => {
    const response = await api.post('/leaves/holidays/default', { year });
    return response.data.data || response.data;
  },

  toggleHolidayStatus: async (id: string): Promise<Holiday> => {
    const response = await api.post(`/leaves/holidays/${id}/toggle`);
    return response.data.data || response.data;
  },

  // Team Calendar (for managers)
  getTeamAttendanceCalendar: async (params: {
    startDate: string;
    endDate: string;
    includeWeekends?: boolean;
  }): Promise<{
    calendar: any[];
    dateRange: {
      startDate: string;
      endDate: string;
    };
  }> => {
    const response = await api.get('/leaves/team-calendar', { params });
    return response.data.data; // Backend returns { success: true, data: {...} }
  },
};
